import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	Button,
	Checkbox,
	Col,
	Drawer,
	Form,
	Input,
	InputNumber,
	Row,
	Spin,
} from "antd";
import { DatePicker, useJalaliLocaleListener } from "antd-jalali";
import PropTypes from "prop-types";
import moment from "moment-jalaali";

import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

const Component = ({ data = {}, onUpdate, options: { courses = [] } }) => {
	const initialState = { editTime: false };
	const [state, setState] = useState(initialState);
	const { editTime, loading, submitting, visible } = state;
	const { id, title } = data;
	const { hash, pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));
				if (values.expiresAt) {
					values.expiresAt = moment(values.expiresAt.$d).format(
						process.env.REACT_APP_DATE_TIME_FORMAT
					);
				}

				if (hash === `#${Modals.EDIT}`) values.expiresAt = data.expiresAt;

				values.usableNumber = values.usableNumber || 0;

				const action =
					hash === `#${Modals.CREATE}`
						? API.coupon.create(values)
						: API.coupon.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, submitting: false }));
					})
					.finally(() => {
						setState((prevState) => ({ ...prevState, submitting: false }));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.coupon
						.details(id)
						.then(({ data }) => {
							form.setFieldsValue({
								...data,
								expiresAt:
									data.expiresAt &&
									moment(data.expiresAt).format(
										process.env.REACT_APP_DATE_TIME_FORMAT_JALALI
									),
							});
						})
						.finally(() => {
							setState((prevState) => ({ ...prevState, loading: false }));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	useJalaliLocaleListener();

	return (
		<Drawer
			className="sp-form-drawer"
			placement={process.env.REACT_APP_IS_RTL ? "left" : "right"}
			footer={
				<>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={submitting}
					>
						{Resources.confirm}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						disabled={submitting}
					>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={
				hash === `#${Modals.CREATE}`
					? `${Resources.add} ${Resources.discount}`
					: `${Resources.edit} ${Resources.discount} "${title}"`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form
				layout="vertical"
				form={form}
			>
				{loading ? (
					<Spin className="sp-center-spin" />
				) : (
					<>
						<Form.Item
							name="title"
							label={Resources.title}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="discount"
							label={`${Resources.percent} ${Resources.discount}`}
							rules={Rules.required}
							help={Resources.couponGuide}
						>
							<InputNumber
								min={1}
								max={100}
							/>
						</Form.Item>
						<Form.Item
							name="usableNumber"
							label={`${Resources.count} ${Resources.usable}`}
						>
							<InputNumber min={1} />
						</Form.Item>
						<Form.Item
							name="expiresAt"
							label={`${Resources.date} ${Resources.expire}`}
						>
							{editTime ? (
								<DatePicker
									allowClear
									showTime
								/>
							) : (
								<Input
									onClick={() => {
										setState((prevState) => ({ ...prevState, editTime: true }));
										form.setFieldValue("expiresAt", "");
									}}
								/>
							)}
						</Form.Item>
						<Form.Item
							name="description"
							label={Resources.description}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
						<Form.Item
							name="courses"
							label={`${Resources.course}${Resources.ha}`}
							rules={Rules.required}
						>
							<Checkbox.Group>
								<Row gutter={[16, 8]}>
									{courses.map(({ label, value }) => (
										<Col
											xs={24}
											key={value}
										>
											<Checkbox value={value}>{label}</Checkbox>
										</Col>
									))}
								</Row>
							</Checkbox.Group>
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
